import type { BasemapId, BasemapOption } from '../lib/basemap';
import type { Preferences } from '../lib/preferences';
import { rampGradient } from '../lib/radar-legend';
import { FloatingPanel } from './FloatingPanel';
import { RadarLegend } from './RadarLegend';
import styles from './RadarControls.module.css';

export interface RadarControlsProps {
  preferences: Preferences;
  /** Basemaps this deployment can actually load — satellite needs a token. */
  basemaps: readonly BasemapOption[];
  onChange: (patch: Partial<Preferences>) => void;
  /** Separate from `onChange`: switching basemap also re-seeds the dim. */
  onBasemapChange: (basemap: BasemapId) => void;
}

/**
 * Everything about how the map is drawn: which basemap, how far back it sits,
 * and how the radar on top of it is coloured.
 *
 * It lives in a panel the viewer can drag, because every fixed position on a
 * map is the wrong one for somebody — see `FloatingPanel`.
 */
export function RadarControls({
  preferences,
  basemaps,
  onChange,
  onBasemapChange,
}: RadarControlsProps) {
  const { basemap, basemapDim, radarOpacity, smooth, showSnow, showCoverage } = preferences;

  return (
    <FloatingPanel id="radar-controls" title="Map & radar" dock={{ top: '1rem', left: '1rem' }}>
      <div className={styles.sections}>
        <section className={styles.section}>
          <h2 className={styles.heading}>Basemap</h2>

          <div className={styles.segmented} role="group" aria-label="Basemap">
            {basemaps.map((option) => (
              <button
                key={option.id}
                type="button"
                className={styles.segment}
                aria-pressed={basemap === option.id}
                title={option.hint}
                onClick={() => onBasemapChange(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/*
            A colour basemap and the radar palette compete for the same
            attention — green rain over green land is genuinely hard to read.
            Dimming keeps the geography and pushes it behind the weather, which
            is a trade every viewer wants at a slightly different setting.
          */}
          <Slider
            label="Dim"
            value={basemapDim}
            onChange={(value) => onChange({ basemapDim: value })}
            ariaLabel="How far to dim the basemap behind the radar"
          />
        </section>

        <section className={styles.section}>
          <h2 className={styles.heading}>Radar</h2>

          {/* Zero is allowed: "get the radar out of the way" is a real request. */}
          <Slider
            label="Opacity"
            value={radarOpacity}
            onChange={(value) => onChange({ radarOpacity: value })}
            ariaLabel="Radar overlay opacity"
            trackImage={rampGradient()}
          />

          <RadarLegend showSnow={showSnow} />

          <div className={styles.switches}>
            <Switch
              label="Smooth"
              hint="Blend between intensity steps instead of hard banding"
              checked={smooth}
              onChange={(value) => onChange({ smooth: value })}
            />
            <Switch
              label="Snow"
              hint="Draw frozen precipitation in its own palette"
              checked={showSnow}
              onChange={(value) => onChange({ showSnow: value })}
            />
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.heading}>Coverage</h2>

          <Switch
            label="Show gaps in coverage"
            checked={showCoverage}
            onChange={(value) => onChange({ showCoverage: value })}
          />

          {/*
            Stated plainly because the failure it prevents is a silent one: with
            the scrim off, a gap in the radar network is indistinguishable from
            clear weather, and the natural reading of an empty map is "no rain".
          */}
          <p className={styles.note}>
            {showCoverage
              ? 'Dimmed areas have no radar watching them — conditions there are unknown, not clear.'
              : 'Radar covers only part of the world. Turn this on to see where it does not.'}
          </p>

          {/*
            The scrim makes a true claim — dimmed means unknown — but a binary
            overlay invites the converse, that undimmed means observed, and that
            one does not hold. RainViewer's mask is built from a declared station
            list, so an area can sit inside coverage while no radar there is
            returning anything, and the scrim then converts missing information
            into apparent assurance. Kept as a disclosure rather than a fourth
            sentence: the primary claim is what most readers need, and burying
            it in caveats is how people stop reading the panel at all.
          */}
          {showCoverage && (
            <details className={styles.disclosure}>
              <summary className={styles.disclosureSummary}>Reported, not verified</summary>
              <p className={styles.note}>
                Coverage is what the radar networks report they cover, not a check that data
                arrived. A station inside the undimmed area may not be returning anything, which
                looks identical to clear weather. Where that has been confirmed the area is dimmed
                anyway, but only for the places someone has checked.
              </p>
            </details>
          )}
        </section>
      </div>
    </FloatingPanel>
  );
}

interface SliderProps {
  label: string;
  /** 0–1, shown as a whole percentage. */
  value: number;
  onChange: (value: number) => void;
  ariaLabel: string;
  /** Paints the ramp into the track, so the control shows what it controls. */
  trackImage?: string;
}

function Slider({ label, value, onChange, ariaLabel, trackImage }: SliderProps) {
  const percent = Math.round(value * 100);

  return (
    <label className={styles.slider}>
      <span className={styles.sliderLabel}>{label}</span>
      <input
        type="range"
        className={styles.range}
        style={trackImage ? ({ '--track-bg': trackImage } as React.CSSProperties) : undefined}
        min={0}
        max={100}
        // Stepped by one so the thumb can land on the value shown beside it —
        // a coarser step reports a position the readout disagrees with.
        step={1}
        value={percent}
        onChange={(event) => onChange(Number(event.target.value) / 100)}
        aria-label={ariaLabel}
      />
      <span className={styles.sliderValue}>{percent}%</span>
    </label>
  );
}

interface SwitchProps {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

/** A checkbox wearing a switch. The input stays real, so keyboards work. */
function Switch({ label, hint, checked, onChange }: SwitchProps) {
  return (
    <label className={styles.switch} title={hint}>
      <input
        type="checkbox"
        className={styles.switchInput}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className={styles.switchTrack} aria-hidden="true" />
      <span className={styles.switchLabel}>{label}</span>
    </label>
  );
}
